import { useState, useEffect, useCallback } from 'react';
import ServerTimeService from '../services/serverTimeService';

interface UseServerTimeReturn {
  serverTime: Date;
  isSynced: boolean;
  lastSync: Date | null;
  offset: number;
  syncServerTime: () => Promise<boolean>;
  formatServerTime: (format?: 'full' | 'time' | 'date') => string;
  compareWithClientTime: () => number;
}

/**
 * Hook pour utiliser l'heure serveur synchronisée
 */
export const useServerTime = (autoSync: boolean = true, syncInterval: number = 60000): UseServerTimeReturn => {
  const [serverTime, setServerTime] = useState<Date>(new Date());
  const [isSynced, setIsSynced] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [offset, setOffset] = useState<number>(0);

  const serverTimeService = ServerTimeService.getInstance();

  const syncServerTime = useCallback(async (): Promise<boolean> => {
    const success = await serverTimeService.syncServerTime();
    if (success) {
      setIsSynced(true);
      setLastSync(serverTimeService.getLastSync());
      setOffset(serverTimeService.getOffset());
      setServerTime(serverTimeService.getServerTime());
    }
    return success;
  }, [serverTimeService]);

  // Synchronisation initiale
  useEffect(() => {
    syncServerTime();
  }, [syncServerTime]);

  // Mise à jour de l'heure toutes les secondes
  useEffect(() => {
    const interval = setInterval(() => {
      if (isSynced) {
        setServerTime(serverTimeService.getServerTime());
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isSynced, serverTimeService]);

  // Synchronisation automatique périodique
  useEffect(() => {
    if (autoSync && isSynced) {
      serverTimeService.startAutoSync(syncInterval);
      return () => {
        serverTimeService.stopAutoSync();
      };
    }
  }, [autoSync, isSynced, syncInterval, serverTimeService]);

  const formatServerTime = useCallback((format: 'full' | 'time' | 'date' = 'full'): string => {
    return serverTimeService.formatServerTime(format);
  }, [serverTimeService]);

  const compareWithClientTime = useCallback((): number => {
    return serverTimeService.compareWithClientTime();
  }, [serverTimeService]);

  return {
    serverTime,
    isSynced,
    lastSync,
    offset,
    syncServerTime,
    formatServerTime,
    compareWithClientTime,
  };
};

export default useServerTime;

